import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Button, Chip, CircularProgress, MenuItem, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography, Alert, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, Divider, useMediaQuery, useTheme
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { reconciliationsAPI } from '../services/api';

const STATUS_COLORS = {
  MATCHED: 'success',
  AMOUNT_MISMATCH: 'warning',
  MISSING_LOCAL: 'error',
  MISSING_IN_BANK: 'error',
  MISMATCHED_BUILDING: 'warning'
};

const STATUS_OPTIONS = [
  '', 'MATCHED', 'AMOUNT_MISMATCH', 'MISSING_LOCAL', 'MISSING_IN_BANK', 'MISMATCHED_BUILDING'
];

const FAILED_RECON_STATUSES = new Set([
  'FAILED', 'AMOUNT_MISMATCH', 'MISSING_LOCAL', 'MISSING_IN_BANK', 'MISMATCHED_BUILDING'
]);

function normalizeStatus(status) {
  return String(status || '').toUpperCase();
}

function isoDaysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

export default function ReconciliationPanel({ t, buildingId }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [from, setFrom] = useState(isoDaysAgo(30));
  const [to, setTo] = useState(isoDaysAgo(0));
  const [statusFilter, setStatusFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState(25);
  const [cursor, setCursor] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [cursorStack, setCursorStack] = useState([]);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [uploading, setUploading] = useState(false);
  const [overrideDialog, setOverrideDialog] = useState(null);
  const [overriding, setOverriding] = useState(false);

  const tr = (k, fb) => (t ? t(`buildingAdmin.reconciliation.${k}`, fb) : fb);

  const load = useCallback(async () => {
    if (!buildingId) {
      setRows([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await reconciliationsAPI.list({
        buildingId,
        from,
        to,
        limit: pageSize,
        ...(cursor ? { cursor } : {}),
        ...(statusFilter ? { status: statusFilter } : {}),
        ...(search ? { search } : {})
      });
      setRows(res.data?.items || []);
      setNextCursor(res.data?.nextCursor || null);
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, [buildingId, from, to, statusFilter, search, pageSize, cursor]);

  useEffect(() => {
    setCursor(null);
    setNextCursor(null);
    setCursorStack([]);
    setPage(1);
  }, [buildingId, from, to, statusFilter, search, pageSize]);

  useEffect(() => { load(); }, [load]);

  const refresh = () => {
    const nextSearch = searchInput.trim();
    if (nextSearch === search) {
      load();
    } else {
      setSearch(nextSearch);
    }
  };

  const goNext = () => {
    if (!nextCursor) return;
    setCursorStack(prev => [...prev, cursor]);
    setCursor(nextCursor);
    setPage(prev => prev + 1);
  };

  const goPrev = () => {
    if (cursorStack.length === 0) return;
    const previousStack = [...cursorStack];
    const previousCursor = previousStack.pop() || null;
    setCursorStack(previousStack);
    setCursor(previousCursor);
    setPage(prev => Math.max(1, prev - 1));
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setUploading(true);
    setError('');
    setInfo('');
    try {
      const presignedRes = await reconciliationsAPI.getUploadUrl(file.name, buildingId);
      await reconciliationsAPI.uploadToS3(presignedRes.data, file);
      setInfo(tr('uploadSuccess', 'Report uploaded. It will be processed shortly.'));
      setTimeout(load, 4000);
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally {
      setUploading(false);
    }
  };

  const exportCsv = () => {
    if (!rows.length) return;
    const headers = [
      tr('settlementDate', 'Settlement Date'), tr('paymentId', 'Payment ID'), tr('flat', 'Flat'),
      tr('localAmount', 'Local Amount'), tr('bankAmount', 'Bank Amount'), tr('msf', 'MSF'),
      tr('net', 'Net'), tr('upiTrxnId', 'UPI Trxn ID'),
      tr('paymentStatus', 'Payment Status'), tr('statusCol', 'Status')
    ];
    const csv = [headers.join(',')].concat(rows.map(r => [
      r.settlementDate || '',
      r.paymentId || '',
      flatText(r),
      r.localAmount ?? '',
      r.bankAmount ?? '',
      r.bankFee ?? '',
      r.bankNet ?? '',
      r.upiTrxnId || '',
      r.paymentStatus || '',
      r.reconStatus || ''
    ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reconciliations-${from}-to-${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getOverrideAction = (row) => {
    const paymentStatus = normalizeStatus(row.paymentStatus);
    const reconStatus = normalizeStatus(row.reconStatus);
    if (['PENDING', 'FAILED'].includes(paymentStatus) && ['MATCHED', 'SUCCESS'].includes(reconStatus)) {
      return {
        targetStatus: 'COMPLETED',
        label: tr('markCompleted', 'Mark Completed'),
        icon: <CheckCircleIcon />
      };
    }
    if (paymentStatus === 'COMPLETED' && FAILED_RECON_STATUSES.has(reconStatus)) {
      return {
        targetStatus: 'FAILED',
        label: tr('markFailed', 'Mark Failed'),
        icon: <CancelIcon />
      };
    }
    return null;
  };

  const shortText = (value, length = 18) => {
    const text = String(value || '');
    if (!text) return '-';
    return text.length > length ? `${text.slice(0, length)}...` : text;
  };

  const moneyText = (value) => (
    value === null || value === undefined || value === '' ? '-' : value
  );

  const flatText = (row) => row.flatNumber || '-';

  const renderReconStatus = (row) => (
    <Chip
      size="small"
      label={row.reconStatus || '-'}
      color={STATUS_COLORS[row.reconStatus] || 'default'}
      sx={{ fontWeight: 600, maxWidth: '100%' }}
    />
  );

  const renderPaymentStatus = (row) => (
    <Chip
      size="small"
      label={row.paymentStatus || '-'}
      variant="outlined"
      sx={{ maxWidth: '100%' }}
    />
  );

  const renderMobileField = (label, value, valueSx = {}) => (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          mt: 0.25,
          fontWeight: 600,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          ...valueSx
        }}
      >
        {value}
      </Typography>
    </Box>
  );

  const openOverrideDialog = (row, action) => {
    setOverrideDialog({ row, action });
    setError('');
    setInfo('');
  };

  const renderOverrideAction = (row, compact = false) => {
    const action = getOverrideAction(row);
    if (!action) return '-';
    return (
      <Button
        size="small"
        variant="outlined"
        startIcon={compact ? null : action.icon}
        onClick={() => openOverrideDialog(row, action)}
        title={action.label}
        sx={{
          minWidth: compact ? 0 : undefined,
          px: compact ? 1.25 : undefined,
          whiteSpace: 'nowrap'
        }}
      >
        {action.label}
      </Button>
    );
  };

  const applyOverride = async () => {
    if (!overrideDialog) return;
    setOverriding(true);
    setError('');
    setInfo('');
    try {
      await reconciliationsAPI.overridePaymentStatus({
        buildingId,
        paymentId: overrideDialog.row.paymentId,
        targetStatus: overrideDialog.action.targetStatus,
        reason: 'Building admin reconciliation override'
      });
      setInfo(tr('overrideSuccess', 'Payment status updated and audit logs created.'));
      setOverrideDialog(null);
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally {
      setOverriding(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {tr('title', 'Payment Reconciliation')}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {info  && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo('')}>{info}</Alert>}

      <Box
        sx={{
          mb: 2,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.25,
          alignItems: 'center',
          '& .MuiInputBase-root': { height: 40 }
        }}
      >
        <TextField
          type="date"
          label={tr('from', 'From')}
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
          sx={{ width: { xs: '100%', sm: 160 } }}
        />
        <TextField
          type="date"
          label={tr('to', 'To')}
          value={to}
          onChange={(e) => setTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
          sx={{ width: { xs: '100%', sm: 160 } }}
        />
        <TextField
          select
          label={tr('status', 'Status')}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          size="small"
          sx={{ width: { xs: '100%', sm: 220 } }}
        >
          {STATUS_OPTIONS.map(s => (
            <MenuItem key={s || 'all'} value={s}>{s || tr('all', 'All')}</MenuItem>
          ))}
        </TextField>
        <TextField
          label={tr('search', 'Search')}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') refresh();
          }}
          size="small"
          sx={{ width: { xs: '100%', sm: 260, md: 300 } }}
        />
        <TextField
          select
          label={tr('pageSize', 'Rows')}
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
          size="small"
          sx={{ width: { xs: '100%', sm: 90 } }}
        >
          {[10, 25, 50, 100].map(size => (
            <MenuItem key={size} value={size}>{size}</MenuItem>
          ))}
        </TextField>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
            alignItems: 'center',
            ml: { lg: 'auto' },
            width: { xs: '100%', md: 'auto' }
          }}
        >
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={refresh}
            disabled={loading}
            sx={{ minHeight: 40, whiteSpace: 'nowrap', minWidth: 108 }}
          >
            {tr('refresh', 'Refresh')}
          </Button>
          <Button
            variant="contained"
            component="label"
            startIcon={<UploadFileIcon />}
            disabled={uploading || true}
            title={tr('uploadDisabled', 'Upload is currently disabled')}
            sx={{ minHeight: 40, whiteSpace: 'nowrap', minWidth: 112 }}
          >
            {tr('upload', 'Upload')}
            <input
              hidden
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={handleUpload}
            />
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={exportCsv}
            disabled={!rows.length}
            sx={{ minHeight: 40, whiteSpace: 'nowrap', minWidth: 92 }}
          >
            {tr('csv', 'CSV')}
          </Button>
        </Box>
      </Box>

      <Paper variant="outlined">
        {isMobile ? (
          <Box sx={{ p: 1.25, display: 'grid', gap: 1.25 }}>
            {loading && (
              <Box sx={{ py: 3, textAlign: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            )}
            {!loading && rows.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                {tr('noRows', 'No reconciliation rows for the selected range')}
              </Typography>
            )}
            {!loading && rows.map(r => (
              <Paper key={`${r.PK}#${r.SK}`} variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary">
                      {tr('settlementDate', 'Settlement Date')}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {r.settlementDate || '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ flexShrink: 0, maxWidth: '52%' }}>
                    {renderReconStatus(r)}
                  </Box>
                </Box>

                <Divider sx={{ my: 1.25 }} />

                <Box sx={{ display: 'grid', gap: 1.25 }}>
                  {renderMobileField(
                    tr('paymentId', 'Payment ID'),
                    shortText(r.paymentId, 24),
                    { fontFamily: 'monospace' }
                  )}
                  {renderMobileField(tr('flat', 'Flat'), flatText(r))}
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1 }}>
                    {renderMobileField(tr('localAmount', 'Local Rs'), moneyText(r.localAmount))}
                    {renderMobileField(tr('bankAmount', 'Bank Rs'), moneyText(r.bankAmount))}
                    {renderMobileField(tr('net', 'Net Rs'), moneyText(r.bankNet))}
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        {tr('paymentStatus', 'Payment Status')}
                      </Typography>
                      {renderPaymentStatus(r)}
                    </Box>
                    {renderMobileField(
                      tr('upiTrxnId', 'UPI Trxn ID'),
                      shortText(r.upiTrxnId, 16),
                      { fontFamily: 'monospace' }
                    )}
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                      {tr('actions', 'Actions')}
                    </Typography>
                    {renderOverrideAction(r)}
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        ) : (
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table
            size="small"
            sx={{
              minWidth: 900,
              tableLayout: 'fixed',
              '& th, & td': { px: 1.25 },
              '& th': { whiteSpace: 'nowrap', fontWeight: 700 },
              '& td': { verticalAlign: 'middle' }
            }}
          >
            <colgroup>
              <col style={{ width: '12%' }} />
              <col style={{ width: '16%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '13%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '10%' }} />
            </colgroup>
            <TableHead>
              <TableRow>
                <TableCell>{tr('settlementDate', 'Settlement Date')}</TableCell>
                <TableCell>{tr('paymentId', 'Payment ID')}</TableCell>
                <TableCell>{tr('flat', 'Flat')}</TableCell>
                <TableCell align="right">{tr('localAmount', 'Local ₹')}</TableCell>
                <TableCell align="right">{tr('bankAmount', 'Bank Txn ₹')}</TableCell>
                <TableCell align="right">{tr('net', 'Net ₹')}</TableCell>
                <TableCell>{tr('actions', 'Actions')}</TableCell>
                <TableCell>{tr('upiTrxnId', 'UPI Trxn ID')}</TableCell>
                <TableCell>{tr('paymentStatusShort', 'Pay Status')}</TableCell>
                <TableCell>{tr('statusCol', 'Status')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={10} align="center"><CircularProgress size={24} /></TableCell>
                </TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    {tr('noRows', 'No reconciliation rows for the selected range')}
                  </TableCell>
                </TableRow>
              )}
              {!loading && rows.map(r => (
                <TableRow key={`${r.PK}#${r.SK}`}>
                  <TableCell>{r.settlementDate || '-'}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                    <Tooltip title={r.paymentId || ''}>
                      <span>{(r.paymentId || '').slice(0, 18)}…</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{flatText(r)}</TableCell>
                  <TableCell align="right">{r.localAmount ?? '-'}</TableCell>
                  <TableCell align="right">{r.bankAmount ?? '-'}</TableCell>
                  <TableCell align="right">{r.bankNet ?? '-'}</TableCell>
                  <TableCell>{renderOverrideAction(r, true)}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                    <Tooltip title={r.upiTrxnId || ''}>
                      <span>{shortText(r.upiTrxnId, 14)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {renderPaymentStatus(r)}
                  </TableCell>
                  <TableCell>
                    {renderReconStatus(r)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        )}
        <Box
          sx={{
            p: 1.5,
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            justifyContent: 'flex-end',
            flexWrap: 'wrap'
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {tr('page', 'Page')} {page}
          </Typography>
          <Button variant="outlined" size="small" onClick={goPrev} disabled={loading || cursorStack.length === 0}>
            {tr('previous', 'Previous')}
          </Button>
          <Button variant="outlined" size="small" onClick={goNext} disabled={loading || !nextCursor}>
            {tr('next', 'Next')}
          </Button>
        </Box>
      </Paper>

      <Dialog open={Boolean(overrideDialog)} onClose={() => !overriding && setOverrideDialog(null)}>
        <DialogTitle>{tr('confirmOverrideTitle', 'Confirm status update')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {tr(
              'confirmOverrideMessage',
              'This will update the payment status, recalculate the flat outstanding balance, and create audit log entries.'
            )}
          </DialogContentText>
          {overrideDialog && (
            <Box sx={{ mt: 2, display: 'grid', gap: 0.75 }}>
              <Typography variant="body2">
                {tr('paymentId', 'Payment ID')}: {overrideDialog.row.paymentId}
              </Typography>
              <Typography variant="body2">
                {tr('paymentStatus', 'Payment Status')}: {overrideDialog.row.paymentStatus || '-'} -&gt; {overrideDialog.action.targetStatus}
              </Typography>
              <Typography variant="body2">
                {tr('statusCol', 'Status')}: {overrideDialog.row.reconStatus || '-'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverrideDialog(null)} disabled={overriding}>
            {tr('cancel', 'Cancel')}
          </Button>
          <Button onClick={applyOverride} variant="contained" disabled={overriding}>
            {overriding ? tr('updating', 'Updating...') : tr('confirm', 'Confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
